import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button, Icon } from 'components/ui';
import { FileUploader } from '../components/shared/FileUploader';
import { OnboardingData } from '../types';
import { uploadFilesToStorage, autofillOnboarding } from 'lib/supabase-db';

type Step0State = 'choice' | 'upload' | 'processing' | 'success';

interface UploadedFile {
  file: File;
  id: string;
  name: string;
  size: number;
}

interface Step0GatewayProps {
  onNavigateToStep?: (step: number) => void;
}

export const Step0Gateway: React.FC<Step0GatewayProps> = ({ onNavigateToStep }) => {
  const { control, setValue, reset } = useFormContext<OnboardingData>();
  const navigate = useNavigate();
  const [state, setState] = useState<Step0State>('choice');
  const [error, setError] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<OnboardingData | null>(null);
  const [uploadedFileInfo, setUploadedFileInfo] = useState<
    Array<{ url: string; name: string; size?: number }>
  >([]);

  // Watch the temp_files field to track uploaded files
  const files: UploadedFile[] = useWatch({ control, name: 'temp_files' as any }) || [];

  // Navigate to manual onboarding (Step 1)
  const handleManualFlow = () => {
    if (onNavigateToStep) {
      onNavigateToStep(1);
    } else {
      navigate('/onboarding?step=1');
    }
  };

  // User chooses autofill
  const handleAutofillChoice = () => {
    setState('upload');
    setError(null);
  };

  // Process files and extract data
  const handleExtractData = async () => {
    if (files.length === 0) {
      setError('Please upload at least one file to continue');
      return;
    }

    setState('processing');
    setError(null);

    try {
      // Step 1: Upload files to storage
      console.log('Uploading files to storage...');
      const actualFiles = files.map((f) => f.file);
      const fileUrls = await uploadFilesToStorage(actualFiles);
      console.log('Files uploaded:', fileUrls);

      // Step 2: Call autofill API
      console.log('Calling autofill API...');
      const response = await autofillOnboarding(fileUrls);
      console.log('Autofill response:', response);

      if (!response.success) {
        throw new Error('Failed to extract data from files');
      }

      // Step 3: Store extracted data and file info
      setExtractedData(response.onboarding_data);
      setUploadedFileInfo(response.uploaded_files || []);
      setState('success');
    } catch (err) {
      console.error('Error during autofill:', err);
      setError(err instanceof Error ? err.message : 'Failed to process files');
      setState('upload');
    }
  };

  // User reviews data and proceeds to Step 1
  const handleReviewData = () => {
    if (!extractedData) return;

    // Convert uploaded file info to UploadedFile format for Step 7
    const uploadedFiles = uploadedFileInfo.map((fileInfo) => ({
      file: {}, // Empty File object (already uploaded to storage)
      id: crypto.randomUUID(),
      name: fileInfo.name,
      size: fileInfo.size || 0,
      storageUrl: fileInfo.url, // Store the storage URL
    }));

    // Pre-fill form with extracted data INCLUDING files
    const completeData = {
      ...extractedData,
      step7_brandAssets: {
        ...extractedData.step7_brandAssets,
        files: uploadedFiles, // Add autofilled files to Step 7
      },
    };

    reset(completeData);

    // Navigate to Step 1 for review
    if (onNavigateToStep) {
      onNavigateToStep(1);
    } else {
      window.location.href = '/onboarding?step=1';
    }
  };

  // Back to choice screen
  const handleBackToChoice = () => {
    setState('choice');
    setValue('temp_files' as any, []); // Clear uploaded files
    setError(null);
  };

  // Render choice screen
  const renderChoiceScreen = () => (
    <div className="max-w-2xl mx-auto py-8">
      <div className="text-center mb-8">
        <h2 className="text-base font-semibold text-gray-900 mb-2">
          How would you like to get started?
        </h2>
        <p className="text-gray-600">
          Choose the option that works best for you. You can always edit the information later.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Autofill Option */}
        <button
          onClick={handleAutofillChoice}
          className="group relative p-8 bg-white border-2 border-gray-200 rounded-xl hover:border-primary hover:shadow-lg transition-all duration-200 text-left cursor-pointer"
        >
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <svg
                className="w-8 h-8 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Autofill from Documents</h3>
              <p className="text-sm text-gray-600">
                Upload your brand documents (PDFs, docs, etc.) and let AI extract the information
                for you. Fast and easy!
              </p>
            </div>
            <div className="pt-2">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                Get Started
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </span>
            </div>
          </div>
        </button>

        {/* Manual Option */}
        <button
          onClick={handleManualFlow}
          className="group relative p-8 bg-white border-2 border-gray-200 rounded-xl hover:border-primary hover:shadow-lg transition-all duration-200 text-left cursor-pointer"
        >
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-gray-200 transition-colors">
              <Icon type="write" className="w-8 h-8 text-gray-700" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Fill Manually</h3>
              <p className="text-sm text-gray-600">
                Prefer to enter your brand information step by step? Take your time and fill out the
                form manually.
              </p>
            </div>
            <div className="pt-2">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                Continue
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </span>
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  // Render upload screen
  const renderUploadScreen = () => (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <button
          onClick={handleBackToChoice}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to options
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Upload Your Brand Documents</h3>
          <p className="text-gray-600">
            Upload documents that contain information about your brand, products, mission, audience,
            and goals. The more comprehensive your documents, the better the results!
          </p>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-700 font-medium mb-3">Recommended documents:</p>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Brand guidelines or style guides</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Marketing materials or brochures</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Business plans or pitch decks</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Product descriptions or documentation</span>
            </li>
          </ul>
        </div>

        <FileUploader
          name="temp_files"
          control={control}
          accept=".pdf,.doc,.docx,.txt"
          maxSize={10 * 1024 * 1024}
          multiple={true}
        />

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="mt-8 flex justify-center gap-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleBackToChoice}
            className="w-1/5"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleExtractData}
            disabled={files.length === 0}
            className="w-1/2 relative"
          >
            Extract Data
            <Icon type="rightArrow" className="w-4 absolute right-5 fill-slate-100" />
          </Button>
        </div>
      </div>
    </div>
  );

  // Render processing screen
  const renderProcessingScreen = () => (
    <div className="max-w-2xl mx-auto py-12">
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-primary animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        </div>
        <h3 className="text-base font-semibold text-gray-900 mb-3">Processing Your Documents...</h3>
        <p className="text-gray-600 mb-6">
          Our AI is analyzing your documents and extracting brand information. This usually takes
          10-30 seconds.
        </p>
        <div className="space-y-2 text-sm text-gray-500">
          <p>✓ Uploading files to secure storage</p>
          <p>✓ Extracting text from documents</p>
          <p className="text-primary font-medium">→ Analyzing brand information...</p>
        </div>
      </div>
    </div>
  );

  // Render success screen
  const renderSuccessScreen = () => (
    <div className="max-w-2xl mx-auto py-12">
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <Icon type="check" className="w-10 h-10 text-green-600" />
          </div>
        </div>
        <h3 className="text-base font-semibold text-gray-900 mb-3">Data Extracted Successfully!</h3>
        <p className="text-gray-600 mb-8">
          We've extracted information from your documents and pre-filled the onboarding form. You
          can now review and edit the information across all steps before finalizing.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Please review all extracted information carefully. You can edit
            any field as needed.
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={handleReviewData}
          className="w-5/6 mx-auto relative"
        >
          Review & Edit Data
          <Icon type="rightArrow" className="w-4 absolute right-5 fill-slate-100" />
        </Button>
      </div>
    </div>
  );

  // Render appropriate screen based on state
  switch (state) {
    case 'choice':
      return renderChoiceScreen();
    case 'upload':
      return renderUploadScreen();
    case 'processing':
      return renderProcessingScreen();
    case 'success':
      return renderSuccessScreen();
    default:
      return renderChoiceScreen();
  }
};
